import { Fragment, ReactNode } from 'react';
import clsx from 'clsx';

import { ExternalLink } from '@@/ExternalLink';

/**
 * Matches http:// and https:// URLs (with optional path/query).
 * Excludes whitespace and characters that are unsafe in URLs.
 */
const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;

/**
 * Matches host-like strings (e.g. example.com, registry.local).
 * Requires at least one dot and a letter-only TLD to avoid false positives (e.g. "3.14").
 */
const HOSTNAME_REGEX =
  /([a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,})/g;

const ALLOWED_PROTOCOLS = ['http:', 'https:'];

/**
 * Converts a string message into ReactNode with URLs and hostnames rendered as safe external links.
 * Only http/https are allowed; hostnames are linked with https.
 * Safe to call with any string (e.g. user-provided policy messages).
 *
 * Known limitations:
 * - Trailing punctuation (e.g. "visit example.com.") is included in the link text/href
 * - IPv6 addresses and punycode/IDN hostnames are not matched
 * - Parenthesised URLs like (https://example.com) include the closing paren
 */
export function linkifyMessage(
  text: string,
  { linkClassName }: { linkClassName?: string } = {}
): ReactNode {
  if (typeof text !== 'string' || text.trim() === '') return text;

  const segments = text.split(URL_REGEX);
  const parts = segments.map((segment, index) =>
    segment === '' ? (
      <span key={`space-${index}`} className="inline">
        {' '}
      </span>
    ) : (
      renderSegment(segment, index, { linkClassName })
    )
  );
  return <>{parts}</>;
}

function renderSegment(
  segment: string,
  key: number,
  { linkClassName }: { linkClassName?: string } = {}
): ReactNode {
  if (segment.startsWith('http://') || segment.startsWith('https://')) {
    if (!isSafeHref(segment)) return <Fragment key={key}>{segment}</Fragment>;
    return (
      <ExternalLink
        key={key}
        to={segment}
        className={clsx('text-primary mx-0.5 hover:underline', linkClassName)}
        data-cy="linkified-link"
      >
        {segment}
      </ExternalLink>
    );
  }

  const hostnameSplits = segment.split(HOSTNAME_REGEX).filter(Boolean);
  if (hostnameSplits.length <= 1)
    return <Fragment key={key}>{segment}</Fragment>;

  const hostPartRegex =
    /^[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
  const nodes: ReactNode[] = hostnameSplits.map((part, idx) => {
    const isHost = part.includes('.') && hostPartRegex.test(part);
    if (isHost) {
      return (
        <ExternalLink
          key={`${key}-${idx}`}
          to={`https://${part}`}
          className={clsx('text-primary mx-0.5 hover:underline', linkClassName)}
          data-cy="linkified-link"
        >
          {part}
        </ExternalLink>
      );
    }
    return part;
  });
  return <Fragment key={key}>{nodes}</Fragment>;
}

function isSafeHref(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ALLOWED_PROTOCOLS.includes(parsed.protocol);
  } catch {
    return false;
  }
}
